/**
 * passport/models.ts — types for Module #1, the Roamer Passport™.
 *
 * A platform MODULE, not a core engine: the permanent record of a user's
 * WorkRoamer journey — stamps, achievements, statistics, and a travel
 * timeline. It integrates with Roamer Core™ only (event subscriptions +
 * publishes), never talks to other engines directly, and never modifies them.
 */

// ─── Stamps ────────────────────────────────────────────────────────────────────

export const STAMP_CATEGORIES = [
  'country',
  'city',
  'island',
  'nationalPark',
  'coworking',
  'communityMeetup',
  'specialEvent',
  'missionCompletion',
  'seasonal',
  'milestone',
] as const;
export type StampCategory = (typeof STAMP_CATEGORIES)[number];

export type StampVerification = 'verified' | 'unverified';

export interface PassportStamp {
  id: string;
  category: StampCategory;
  location: string;
  earnedAt: number;
  description: string;
  /** Placeholder path/slug for future stamp artwork. */
  artworkPlaceholder: string;
  verification: StampVerification;
}

// ─── Achievements ──────────────────────────────────────────────────────────────

export interface PassportAchievement {
  id: string;
  title: string;
  description: string;
  unlockedAt: number;
  xpAwarded: number;
}

// ─── Timeline ──────────────────────────────────────────────────────────────────

export type TimelineEventKind =
  | 'movedTo'
  | 'visited'
  | 'missionCompleted'
  | 'communityJoined'
  | 'achievementEarned'
  | 'stampEarned'
  | 'photoAdded'
  | 'journeyUpdated';

export interface TimelineEvent {
  id: string;
  kind: TimelineEventKind;
  title: string;
  location: string | null;
  at: number;
}

// ─── Roamer Moments ────────────────────────────────────────────────────────────

/** A photo/memory associated with places, stamps, and timeline events. */
export interface RoamerMoment {
  id: string;
  photoRef: string;
  city: string | null;
  country: string | null;
  stampId: string | null;
  timelineEventId: string | null;
  /** Named future memory collections (e.g. "Thailand 2026"). */
  collections: string[];
  at: number;
}

// ─── Statistics ────────────────────────────────────────────────────────────────

export interface PassportStats {
  countriesVisited: number;
  citiesVisited: number;
  currentStreakDays: number;
  longestStreakDays: number;
  missionSuccessRate: number | null;
  averageMonthlyBudgetUsd: number | null;
  /** null until at least two bases carried coordinates — never fabricated. */
  totalDistanceKm: number | null;
  favoriteCountry: string | null;
  favoriteCity: string | null;
  favoriteCoworking: string | null;
  favoriteCafe: string | null;
  averageInternetMbps: number | null;
  daysAbroad: number;
  passportLevel: number;
}

// ─── Profile ───────────────────────────────────────────────────────────────────

export interface StayRecord {
  city: string;
  country: string | null;
  arrivedAt: number;
  departedAt: number | null;
  /** Coordinates when the bus carried them — enables honest distance stats. */
  latitude: number | null;
  longitude: number | null;
}

export interface ScoreHistoryEntry {
  score: number;
  at: number;
}

export interface PassportProfile {
  passportId: string;
  userId: string;
  issuedAt: number;
  currentLevel: number;
  totalXp: number;
  countriesVisited: string[];
  citiesVisited: string[];
  currentBase: { city: string; country: string | null } | null;
  favoriteDestination: string | null;
  longestStay: { city: string; days: number } | null;
  totalDaysRoaming: number;
  missionCompletionCount: number;
  scoreHistory: ScoreHistoryEntry[];
  timeline: TimelineEvent[];
  stamps: PassportStamp[];
  achievements: PassportAchievement[];
  moments: RoamerMoment[];
  stays: StayRecord[];
}

// ─── Privacy ───────────────────────────────────────────────────────────────────

export interface PassportPrivacy {
  /** false = private passport (public view returns nothing). */
  publicPassport: boolean;
  shareTimeline: boolean;
  shareStatistics: boolean;
  shareStamps: boolean;
}

export const DEFAULT_PASSPORT_PRIVACY: PassportPrivacy = {
  publicPassport: false,
  shareTimeline: false,
  shareStatistics: false,
  shareStamps: false,
};

/** What others see — filtered strictly by the privacy settings. */
export interface PublicPassportView {
  passportId: string;
  currentLevel: number;
  timeline: TimelineEvent[] | null;
  stats: PassportStats | null;
  stamps: PassportStamp[] | null;
}

/** Full portable export (Export Passport / future Printable Passport). */
export interface PassportExport {
  exportedAt: number;
  profile: PassportProfile;
  privacy: PassportPrivacy;
  stats: PassportStats;
}

// ─── Levels & XP ───────────────────────────────────────────────────────────────

/**
 * XP thresholds per level (index = level - 1). Extensible for future levels.
 *
 * PROGRESSION DESIGN (Build 2.32.0):
 *   Each level represents a real achievement milestone, not initialization:
 *   – Level 1 "Wanderer"    :     0 XP  — fresh operator, just beginning
 *   – Level 2 "Explorer"    :   300 XP  — ~10 missions completed
 *   – Level 3 "Builder"     : 1 000 XP  — 35+ missions, first cities explored
 *   – Level 4 "Operator"    : 2 500 XP  — 100 missions, multiple countries
 *   – Level 5 "Navigator"   : 6 000 XP  — serious long-term operator
 *   – Level 6 "Pathfinder"  :15 000 XP  — veteran roamer
 *   – Level 7 "Trailblazer" :35 000 XP  — elite
 *   – Level 8 "Legend"      :80 000 XP  — legendary status
 *
 *   A new user should think "I've just started" — not "I've almost finished."
 */
export const LEVEL_THRESHOLDS = [0, 300, 1000, 2500, 6000, 15000, 35000, 80000];

export function levelForXp(xp: number): number {
  let level = 1;
  for (let i = 0; i < LEVEL_THRESHOLDS.length; i++) {
    if (xp >= LEVEL_THRESHOLDS[i]) level = i + 1;
  }
  return level;
}

export const XP_AWARDS = {
  newCountry: 100,
  newCity: 50,
  missionCompleted: 25,
  stampEarned: 10,
  photoMoment: 5,
  communityJoined: 20,
} as const;
